import { doc, getDoc, setDoc, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { db } from '../firebase/config';

export interface FirebaseCacheOptions {
  collection: string;
  docId: string;
  maxAge?: number; // in seconds
  enableRealtimeUpdates?: boolean;
  fallbackToCache?: boolean;
}

export interface FirebaseCachedData<T = any> {
  data: T;
  timestamp: number;
  collection: string;
  docId: string;
  fromCache: boolean;
}

class FirebaseCacheService {
  private realtimeListeners: Map<string, Unsubscribe> = new Map();
  private messageCallbacks: Map<string, (data: any) => void> = new Map();

  constructor() {
    // Listen for messages from service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        const { type, payload } = event.data;
        
        if (type === 'CACHED_FIREBASE_DATA_RESPONSE') {
          const key = `${payload.collection}/${payload.docId}`;
          const callback = this.messageCallbacks.get(key);
          if (callback) {
            callback(payload);
            this.messageCallbacks.delete(key);
          }
        }
      });
    }
  }

  /**
   * Get document with offline capabilities
   * Uses cache-first strategy with Firestore fallback
   */
  async getDocument<T>(options: FirebaseCacheOptions): Promise<FirebaseCachedData<T> | null> {
    const { collection, docId, maxAge = 600, fallbackToCache = true } = options; // 10 minutes default
    const key = `${collection}/${docId}`;

    console.log(`[FIREBASE_CACHE] Getting document: ${key}`);

    // First, try to get from cache
    const cachedData = await this.getFromCache<T>(collection, docId);

    // STEP 1: Return cached data immediately if available (cache-first pattern)
    if (cachedData) {
      const age = (Date.now() - cachedData.timestamp) / 1000;
      console.log(`[FIREBASE_CACHE] Found cached data for ${key}, age: ${age}s - returning immediately`);
      
      // Return cached data immediately, then fetch fresh data in background
      const cachedResult = { ...cachedData, fromCache: true };
      
      // STEP 2: Always fetch fresh data in background to ensure latest updates
      console.log(`[FIREBASE_CACHE] Fetching fresh data in background for ${key}`);
      this.fetchAndUpdateCache<T>(collection, docId).catch(error => {
        console.warn(`[FIREBASE_CACHE] Background fetch failed for ${key}:`, error);
      });
      
      return cachedResult;
    }

    // No cached data available - fetch from Firestore synchronously
    try {
      console.log(`[FIREBASE_CACHE] No cache available, fetching from Firestore: ${key}`);
      const docRef = doc(db, collection, docId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data() as T;
        
        // Cache the fresh data
        await this.setCache(collection, docId, data);
        
        console.log(`[FIREBASE_CACHE] Successfully fetched and cached: ${key}`);
        return {
          data,
          timestamp: Date.now(),
          collection,
          docId,
          fromCache: false,
        };
      }

      return null;
    } catch (error) {
      console.error(`[FIREBASE_CACHE] Error fetching ${key}:`, error);
      throw error;
    }
  }

  /**
   * Background fetch and cache update (fire and forget)
   */
  private async fetchAndUpdateCache<T>(collection: string, docId: string): Promise<void> {
    try {
      const docRef = doc(db, collection, docId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data() as T;
        await this.setCache(collection, docId, data);
        console.log(`[FIREBASE_CACHE] Background cache update completed for ${collection}/${docId}`);
      }
    } catch (error) {
      // Don't throw - this is a background operation
      console.warn(`[FIREBASE_CACHE] Background fetch failed for ${collection}/${docId}:`, error);
    }
  }

  /**
   * Set document with caching
   */
  async setDocument<T extends Record<string, any>>(collection: string, docId: string, data: T): Promise<void> {
    const key = `${collection}/${docId}`;
    console.log(`[FIREBASE_CACHE] Setting document: ${key}`);

    try {
      // Save to Firestore
      const docRef = doc(db, collection, docId);
      await setDoc(docRef, data);
      
      // Cache the data
      await this.setCache(collection, docId, data);
      
      console.log(`[FIREBASE_CACHE] Successfully set and cached: ${key}`);
    } catch (error) {
      console.error(`[FIREBASE_CACHE] Error setting ${key}:`, error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time updates with caching
   */
  subscribeToDocument<T>(
    options: FirebaseCacheOptions,
    callback: (data: FirebaseCachedData<T> | null, error?: Error) => void
  ): () => void {
    const { collection, docId, enableRealtimeUpdates = true } = options;
    const key = `${collection}/${docId}`;

    console.log(`[FIREBASE_CACHE] Subscribing to: ${key}`);

    if (!enableRealtimeUpdates) {
      // Just get once and return cleanup function
      this.getDocument<T>(options).then(
        (data) => callback(data),
        (error) => callback(null, error)
      );
      return () => {}; // No-op cleanup
    }

    // Get initial data using cache-first pattern (will show cache immediately, then fetch fresh)
    this.getDocument<T>(options).then(
      (data) => {
        if (data) {
          callback(data);
        }
      },
      (error) => console.warn('Failed to get initial data:', error)
    );

    // Set up real-time listener
    const documentRef = doc(db, collection, docId);
    const unsubscribe = onSnapshot(
      documentRef,
      async (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data() as T;
          
          // Cache the fresh data
          await this.setCache(collection, docId, data);
          
          callback({
            data,
            timestamp: Date.now(),
            collection,
            docId,
            fromCache: false,
          });
        } else {
          callback(null);
        }
      },
      (error: any) => {
        console.error(`[FIREBASE_CACHE] Real-time listener error for ${key}:`, error);
        callback(null, error);
      }
    );

    this.realtimeListeners.set(key, unsubscribe);

    // Return cleanup function
    return () => {
      console.log(`[FIREBASE_CACHE] Unsubscribing from: ${key}`);
      unsubscribe();
      this.realtimeListeners.delete(key);
    };
  }

  /**
   * Clear cached document
   */
  async clearDocumentCache(collection: string, docId: string): Promise<void> {
    const key = `${collection}/${docId}`;
    
    if ('caches' in window) {
      const cache = await caches.open('firebase-data-v1');
      await cache.delete(key);
    }
    
    console.log(`[FIREBASE_CACHE] Cleared cache for: ${key}`);
  }

  /**
   * Clear all Firebase cache
   */
  async clearAllFirebaseCache(): Promise<void> {
    if ('caches' in window) {
      await caches.delete('firebase-data-v1');
      await caches.delete('firestore-v1');
    }
    
    console.log('[FIREBASE_CACHE] Cleared all Firebase cache');
  }

  /**
   * Get cache info for Firebase data
   */
  async getFirebaseCacheInfo(): Promise<{ keys: string[]; size: number }> {
    if (!('caches' in window)) {
      return { keys: [], size: 0 };
    }

    try {
      const cache = await caches.open('firebase-data-v1');
      const keys = await cache.keys();
      
      let totalSize = 0;
      for (const request of keys) {
        const response = await cache.match(request);
        if (response) {
          const text = await response.text();
          totalSize += new Blob([text]).size;
        }
      }

      return {
        keys: keys.map(req => req.url.split('/').pop() || ''),
        size: totalSize,
      };
    } catch (error) {
      console.error('Error getting Firebase cache info:', error);
      return { keys: [], size: 0 };
    }
  }

  /**
   * Check if we're offline
   */
  isOffline(): boolean {
    return !navigator.onLine;
  }

  private async getFromCache<T>(collection: string, docId: string): Promise<FirebaseCachedData<T> | null> {
    return new Promise((resolve) => {
      const key = `${collection}/${docId}`;
      
      this.messageCallbacks.set(key, (payload) => {
        if (payload.found) {
          resolve({
            data: payload.data,
            timestamp: payload.timestamp,
            collection: payload.collection,
            docId: payload.docId,
            fromCache: true,
          });
        } else {
          resolve(null);
        }
      });

      // Send message to service worker
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'GET_CACHED_FIREBASE_DATA',
          payload: { collection, docId },
        });
      } else {
        resolve(null);
      }

      // Timeout after 1 second
      setTimeout(() => {
        if (this.messageCallbacks.has(key)) {
          this.messageCallbacks.delete(key);
          resolve(null);
        }
      }, 1000);
    });
  }

  private async setCache<T>(collection: string, docId: string, data: T): Promise<void> {
    if (!navigator.serviceWorker || !navigator.serviceWorker.controller) {
      console.warn('[FIREBASE_CACHE] Service worker not available for cache update');
      return;
    }

    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'CACHE_FIREBASE_DATA',
        payload: { collection, docId, data },
      });
    }
  }

  /**
   * Cleanup all listeners
   */
  cleanup(): void {
    this.realtimeListeners.forEach((unsubscribe, key) => {
      console.log(`[FIREBASE_CACHE] Cleaning up listener: ${key}`);
      unsubscribe();
    });
    this.realtimeListeners.clear();
    this.messageCallbacks.clear();
  }
}

// Create singleton instance
export const firebaseCacheService = new FirebaseCacheService();

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    firebaseCacheService.cleanup();
  });
} 